<script setup>
import {ref, inject, computed, watch, onMounted} from "vue";
import accountTypes from "@/constants/accountTypes";
import {trackerTypes} from "@/constants/trackers";
import SelectFilter from "@/views/trackers/tracker/SelectFilter.vue";
import CustomDataFilterModal from "@/components/search/CustomDataFilterModal.vue";
import trackersService from "@/services/trackers.service";
import customFieldsService from "@/services/custom-fields.service";
import {useStore} from "vue2-helpers/vuex";
import ecosystemsService from "@/services/ecosystems.service";

const store = useStore();

const tracker = inject('tracker');
const filters = inject('filters');
const resetFilters = inject('resetFilters');
const trackerUpdated = inject('trackerUpdated');
const companiesListUpdated = inject('companiesListUpdated');

const showModal = ref(false);
const trackerCustomDataFilter = ref(null);
const profileCustomDataFilter = ref(null);
const trackerFieldsList = ref({});
const profileFieldsList = ref({});
const trackersList = ref([]);
const ecosystemsList = ref([]);
const topEcosystemsList = ref([]);
const stagesList = ref([]);

const ownerEnterprise = computed(() => tracker.value.company_owner_type === accountTypes.ENTERPRISE);
const hasPipeline = computed(() => !!tracker.value.pipeline_id);
const isEcosystems = computed(() => tracker.value.type === trackerTypes.ECOSYSTEMS);

watch(trackerUpdated, (value) => {
    if (value) {
        getTrackerFieldsForFilter();
    }
});

watch(companiesListUpdated, (value) => {
    if (value) {
        getTopEcosystemsList();
    }
});

onMounted(() => {
    getTrackerFieldsForFilter();
    getProfileFieldsForFilter();
    getTrackersList();
    getTopEcosystemsList();

    if (hasPipeline.value) {
        getStagesList();
    }
});

async function getTrackerFieldsForFilter() {
    const {data} = await trackersService.getDiligenceFieldsForFilter(tracker.value.id);

    if (Object.keys(data).length) {
        trackerFieldsList.value = data;
    }
}

async function getProfileFieldsForFilter() {
    const {data} = await customFieldsService.getNotStaticFields('company_diligence');

    if (Object.keys(data).length) {
        profileFieldsList.value = data;
    }
}

function updateTrackerCustomDataFilter(updatedFilters) {
    filters.trackerFields = updatedFilters;
}

function updateProfileCustomDataFilter(updatedFilters) {
    filters.profileFields = updatedFilters;
}

async function getTrackersList() {
    const {data} = await trackersService.getList(tracker.value.type);
    trackersList.value = data.filter(item => item.id !== tracker.value.id);
}

async function getTopEcosystemsList() {
    if (!isEcosystems.value && store.getters.isEnterprise) {
        const {data} = await ecosystemsService.getTopListForTracker(tracker.value.id);
        topEcosystemsList.value = data;
        ecosystemsList.value = [...topEcosystemsList.value];
    }
}

async function updateEcosystemsList(query) {
    if (query.length) {
        const {data} = await ecosystemsService.getListForFilter(query);
        ecosystemsList.value = data;
    } else {
        ecosystemsList.value = topEcosystemsList.value;
    }
}

async function getStagesList() {
    const {data} = await trackersService.getTrackersStages(tracker.value.id);
    stagesList.value = data;
}

function open() {
    showModal.value = true;
}

defineExpose({open});
</script>

<template>
    <b-modal v-model="showModal" hide-header hide-footer modal-class="modal-filters">
        <form class="form">
            <fieldset>
                <header class="modal-header">
                    <h5 class="modal-title">Filter Tracker</h5>

                    <a @click="resetFilters" class="link">Clear All</a>
                </header>

                <div class="form__item">
                    <div class="form-search">
                        <input v-model="filters.search" type="text" placeholder="Search startups, description or keywords"/>
                    </div>
                </div>

                <label class="form__label">Filter by rating</label>

                <div class="flex-line mb-4">
                    <div class="input-radio">
                        <input
                            type="radio"
                            name="ratingCondition"
                            id="rating-type-all"
                            :checked="filters.ratingFilterState === 'all'"
                            @click="filters.ratingFilterState = 'all'; filters.ratingCondition =''"
                        >
                        <label for="rating-type-all">Show all</label>
                    </div>

                    <div class="input-radio">
                        <input
                            type="radio"
                            name="ratingCondition"
                            id="rating-type-rated"
                            :checked="filters.ratingFilterState === 'rated'"
                            @click="filters.ratingFilterState = 'rated'; filters.ratingCondition =''"
                        >
                        <label for="rating-type-rated">Show rated</label>
                    </div>

                    <div class="input-radio">
                        <input
                            type="radio"
                            name="ratingCondition"
                            id="rating-type-unrated"
                            :checked="filters.ratingFilterState === 'unrated'"
                            @click="filters.ratingFilterState = 'unrated'; filters.ratingCondition =''"
                        >
                        <label for="rating-type-unrated">Show unrated</label>
                    </div>
                </div>

                <template v-if="filters.ratingFilterState === 'rated' && !tracker.has_overall_score">
                    <h6>Rating</h6>
                    <div class="rating-section mb-3">

                        <div class="rating">
                            <span :class="{'rating__current': filters.rating >= 1}" @click="filters.rating = 1"/>
                            <span :class="{'rating__current': filters.rating >= 2}" @click="filters.rating = 2"/>
                            <span :class="{'rating__current': filters.rating >= 3}" @click="filters.rating = 3"/>
                            <span :class="{'rating__current': filters.rating >= 4}" @click="filters.rating = 4"/>
                            <span :class="{'rating__current': filters.rating >= 5}" @click="filters.rating = 5"/>
                        </div>

                        <div class="tags-list tags-list--light">
                            <b-button
                                :class="{'selected': filters.ratingCondition === 'higher'}"
                                :checked="filters.ratingCondition === 'higher'"
                                @click="filters.ratingCondition = 'higher'"
                                class="tags-list__tag"
                            >+ Higher
                            </b-button>

                            <b-button
                                :class="{'selected': filters.ratingCondition === 'lower'}"
                                :checked="filters.ratingCondition === 'lower'"
                                @click="filters.ratingCondition = 'lower'"
                                class="tags-list__tag"
                            >+ Lower
                            </b-button>
                        </div>
                    </div>
                </template>

                <template v-if="!isEcosystems">
                    <div class="mb-4" v-if="Object.keys(trackerFieldsList).length">
                        <label class="form__label">Startup Diligence Fields</label>

                        <a class="link" @click="trackerCustomDataFilter.open()">Select Fields</a>

                        <div class="tags-simple-list" v-if="filters.trackerFields.length">
                            <div class="tags-simple-list__item"
                                 v-for="(field, index) in filters.trackerFields" :key="index">
                                {{ trackerFieldsList[field.id].name }}: {{ field.valueAlias }}

                                <a @click="filters.trackerFields.splice(index, 1); trackerCustomDataFilter.removeFilter(index)">
                                    <span class="tags-simple-list__item__close"></span>
                                </a>
                            </div>
                        </div>
                    </div>

                    <div class="mb-4" v-if="Object.keys(profileFieldsList).length">
                        <label class="form__label">Startup Profile Fields</label>

                        <a class="link" @click="profileCustomDataFilter.open()">Select Fields</a>

                        <div class="tags-simple-list" v-if="filters.profileFields.length">
                            <div class="tags-simple-list__item"
                                 v-for="(field, index) in filters.profileFields" :key="index">
                                {{ profileFieldsList[field.id].name }}: {{ field.valueAlias }}

                                <a @click="filters.profileFields.splice(index, 1); profileCustomDataFilter.removeFilter(index)">
                                    <span class="tags-simple-list__item__close"></span>
                                </a>
                            </div>
                        </div>
                    </div>
                </template>

                <template v-if="tracker.type !== trackerTypes.PUBLIC">
                    <label class="form__label">Curator</label>

                    <div class="input-checkbox mb-3">
                        <input type="checkbox" v-model="filters.addedByMe" id="added-by-me">
                        <label for="added-by-me">Show only {{ isEcosystems ? 'ecosystems' : 'startups' }} added by me</label>
                    </div>
                </template>

                <template v-if="ownerEnterprise && tracker.type === trackerTypes.PRIVATE">
                    <div class="input-checkbox">
                        <input type="checkbox" v-model="filters.addedByEcosystems" id="added-by-ecosystems">
                        <label for="added-by-ecosystems">Added by ecosystems</label>
                    </div>
                </template>
            </fieldset>

            <fieldset class="modal-filters__sidebar">
                <button @click="showModal = false" type="button" aria-label="Close" class="close">×</button>

                <div>
                    <SelectFilter
                        v-if="ownerEnterprise && !isEcosystems"
                        v-model="filters.tags"
                        name="tags"
                        :options="store.getters.tagsList"
                    />

                    <SelectFilter
                        v-model="filters.categories"
                        name="categories"
                        :options="store.state.categories"
                    />

                    <SelectFilter
                        v-model="filters.trackers"
                        name="trackers"
                        :options="trackersList"
                    />

                    <SelectFilter
                        v-if="ownerEnterprise && !isEcosystems"
                        v-model="filters.ecosystems"
                        name="ecosystems"
                        :options="ecosystemsList"
                        @updateOptions="updateEcosystemsList"
                    />

                    <SelectFilter
                        v-if="hasPipeline"
                        v-model="filters.stages"
                        name="stages"
                        :options="stagesList"
                    />
                </div>
            </fieldset>
        </form>

        <CustomDataFilterModal
            v-if="Object.keys(trackerFieldsList).length"
            ref="trackerCustomDataFilter"
            :custom-options="trackerFieldsList"
            @updateCustomDataFilter="updateTrackerCustomDataFilter"
        />

        <CustomDataFilterModal
            v-if="Object.keys(profileFieldsList).length"
            ref="profileCustomDataFilter"
            :custom-options="profileFieldsList"
            @updateCustomDataFilter="updateProfileCustomDataFilter"
        />
    </b-modal>
</template>

<style scoped>

</style>
